<template>
  <v-container fluid>
    <v-row align="center" class="mb-4">
      <v-col cols="12" class="d-flex align-center">
        <v-btn text color="primary" class="mr-3" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
        <h2 class="text-h5 font-weight-bold">{{ receta ? receta.nombre : 'Receta' }}</h2>
        <v-chip v-if="receta && receta.es_combinada" small color="deep-purple" dark class="ml-3">
          <v-icon x-small left>mdi-set-merge</v-icon>Combinada
        </v-chip>
        <v-chip v-if="receta && !receta.activa" small color="grey" dark class="ml-3">Inactiva</v-chip>
        <v-spacer />
        <v-btn
          v-if="receta"
          small color="deep-purple" dark
          class="mr-2"
          @click="editar"
        >
          <v-icon small left>mdi-pencil</v-icon>
          Editar
        </v-btn>
        <v-btn
          v-if="receta"
          small outlined
          :color="receta.activa ? 'grey darken-1' : 'green'"
          :loading="toggling"
          @click="toggleActiva"
        >
          <v-icon small left>{{ receta.activa ? 'mdi-cancel' : 'mdi-check-circle-outline' }}</v-icon>
          {{ receta.activa ? 'Desactivar' : 'Activar' }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12"><v-skeleton-loader type="article" /></v-col>
    </v-row>

    <template v-else-if="receta">
      <v-row>
        <v-col cols="12" md="4">
          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold">Datos</v-card-title>
            <v-card-text>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Máquina</span>
                <span class="caption">{{ receta.maquina_nombre || '—' }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">Tiempo de proceso</span>
                <span class="caption">{{ receta.tiempo_minutos > 0 ? receta.tiempo_minutos + ' min' : '—' }}</span>
              </div>
              <div class="d-flex justify-space-between mb-1">
                <span class="grey--text caption">% costo asignado</span>
                <span class="caption">{{ receta.proporcion_total }}%</span>
              </div>
              <v-alert
                v-if="parseFloat(receta.proporcion_total) < 100"
                type="warning" text dense class="mt-3 mb-0 caption"
              >
                El {{ (100 - parseFloat(receta.proporcion_total)).toFixed(0) }}% restante es merma (no se carga al producto).
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card class="mb-4">
            <v-card-title class="subtitle-1 font-weight-bold red--text text--darken-1">
              <v-icon left color="red darken-1">mdi-tray-arrow-down</v-icon> Consume (por lote)
            </v-card-title>
            <v-simple-table>
              <thead><tr><th>Insumo</th><th class="text-right">Cantidad</th></tr></thead>
              <tbody>
                <tr v-for="c in receta.consumos" :key="'c' + c.id">
                  <td>{{ c.descripcion }}</td>
                  <td class="text-right">{{ num(c.cantidad) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>

          <v-card>
            <v-card-title class="subtitle-1 font-weight-bold green--text text--darken-1">
              <v-icon left color="green darken-1">mdi-tray-arrow-up</v-icon> Produce (por lote)
            </v-card-title>
            <v-simple-table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th class="text-right">Cantidad</th>
                  <th class="text-right">% costo</th>
                  <th class="text-right">Margen</th>
                  <th class="text-right">Precio sugerido</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in salidasOrdenadas" :key="'s' + s.id">
                  <td>
                    <v-icon v-if="s._principal" x-small color="amber darken-2" class="mr-1">mdi-star</v-icon>
                    {{ s.descripcion }}
                    <v-chip v-if="s._relleno" x-small outlined color="teal lighten-1" class="ml-1">relleno</v-chip>
                  </td>
                  <td class="text-right">{{ num(s.cantidad) }}</td>
                  <td class="text-right">{{ s.proporcion_costo }}%</td>
                  <td class="text-right">{{ s.margen_ganancia != null ? s.margen_ganancia + '%' : '—' }}</td>
                  <td class="text-right">
                    <span v-if="parseFloat(s.precio_sugerido) > 0" class="green--text">{{ money(s.precio_sugerido) }}</span>
                    <span v-else class="grey--text">—</span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'RecetaConsultar',
  data() {
    return { receta: null, loading: false, toggling: false };
  },
  computed: {
    salidasOrdenadas() {
      if (!this.receta) return [];
      const arr = this.receta.salidas.slice().sort(
        (a, b) => parseFloat(b.proporcion_costo) - parseFloat(a.proporcion_costo),
      );
      const combinada = arr.length > 1;
      return arr.map((s, i) => ({ ...s, _principal: combinada && i === 0, _relleno: combinada && i > 0 }));
    },
  },
  methods: {
    ...mapActions('recetaProduccion', ['get_receta', 'update_receta']),
    num(v) {
      return Number(parseFloat(v)).toLocaleString('es-AR');
    },
    money(v) {
      if (v == null) return '—';
      return '$' + Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    editar() {
      this.$router.push({ name: 'empresa.produccion.receta.editar', params: { id: this.receta.id } });
    },

    async toggleActiva() {
      this.toggling = true;
      try {
        const res = await this.update_receta({ id: this.receta.id, data: { activa: !this.receta.activa } });
        this.receta = { ...this.receta, activa: res.data.activa };
      } catch (e) {
        console.error(e);
      } finally {
        this.toggling = false;
      }
    },

    async fetchReceta() {
      this.loading = true;
      try {
        const res = await this.get_receta(this.$route.params.id);
        this.receta = res.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchReceta();
  },
};
</script>
